import { useEffect, useRef, type Dispatch, type SetStateAction } from "react"

import type { CoviThreadMeta } from "../model/types"
import { loadHistorySnapshot } from "../utils/historySnapshot"
import { loadOrgState, saveOrgState } from "../utils/storage"

interface HistoryRegistry {
  orgSlug: string | null
  threads: CoviThreadMeta[]
  activeThreadId: string | null
}

/** Restore one completed staging conversation without replacing local state. */
export function useCoviHistorySnapshot<T extends HistoryRegistry>(input: {
  enabled: boolean
  orgSlug: string | null
  registry: T
  setRegistry: Dispatch<SetStateAction<T>>
}): void {
  const { enabled, orgSlug, registry, setRegistry } = input
  const registryRef = useRef(registry)
  registryRef.current = registry
  const attemptsRef = useRef(new Set<string>())
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled || !orgSlug || registry.orgSlug !== orgSlug) {
      return
    }
    if (attemptsRef.current.has(orgSlug)) {
      return
    }

    attemptsRef.current.add(orgSlug)
    void loadHistorySnapshot(orgSlug)
      .then((snapshot) => {
        if (!mountedRef.current || !snapshot) {
          return
        }
        const current = registryRef.current
        const currentBlank =
          current.orgSlug === orgSlug &&
          current.threads.length === 1 &&
          current.threads[0].messageCount === 0
            ? current.threads[0]
            : null
        if (
          !currentBlank ||
          loadOrgState(orgSlug, currentBlank.id).items.length > 0 ||
          !saveOrgState(orgSlug, { items: snapshot.items }, snapshot.thread.id)
        ) {
          return
        }
        const next = {
          ...current,
          threads: [snapshot.thread],
          activeThreadId: snapshot.thread.id,
        }
        registryRef.current = next
        setRegistry(next)
      })
      .catch(() => undefined)
  }, [enabled, orgSlug, registry.orgSlug, setRegistry])
}
